import React from 'react';
import PropTypes from 'prop-types';

import Segment, { SegmentConfig } from '@sparefoot/segment-react';
import { getSegmentContext } from '@sparefoot/react-core/segment';

import classnames from 'classnames';
import {
	compose, withState, defaultProps, withPropsOnChange, withHandlers
} from 'recompose';

import { Text } from 'components/core/Typography';
import { Chevron } from 'components/svg';
import { MultiSelectOption } from './MultiSelectOption';

import './MultiSelect.scss';

export const enhance = compose(
	getSegmentContext,
	defaultProps({
		selected: [],
		defaultLabel: 'Make a selection'
	}),
	withState('menuExpanded', 'setMenuExpanded', false),
	withHandlers({
		toggleMenuExpanded: ({ setMenuExpanded, menuExpanded }) => () => {
			setMenuExpanded(!menuExpanded);
		},
		closeMenu: ({ setMenuExpanded }) => () => setMenuExpanded(false),
		openMenu: ({ setMenuExpanded }) => () => setMenuExpanded(true)
	}),
	withHandlers({
		handleChange: ({
			segmentConfig, segmentLabel, selected, multiple, onChange, closeMenu
		}) => (event) => {
			const { target: { value, checked } } = event;
			// don't close this if they might select multiple options
			if (!multiple) closeMenu();


			// this is a controlled input, so its state is managed somewhere else.
			// this will return an updated `selected` array to be stored in parent
			// state and passed back down

			let updatedItems = [];
			// it's false, remove it from the array
			if (!checked) updatedItems = selected.filter((f) => f !== value);
			// if checked and a multiple, add it to the array
			else if (multiple) updatedItems = [...selected, value];
			// if it's checked and not multiple, replace the selected items with it
			else updatedItems = [value];

			const trackingProps = SegmentConfig.buildTrackingProps(
				segmentConfig,
				{ segmentLabel }
			);

			Segment.track('change', trackingProps);
			onChange(updatedItems, event.target);
		}
	}),
	// make sure we always pass an array
	withPropsOnChange(['selected'], ({ selected }) => ({
		selected: Array.isArray(selected) ? selected : [selected]
	})),
	// provide the selected Label depending on how many items are selected
	withPropsOnChange(['selected'], ({ selected, defaultLabel, options }) => {
		// if there's several items selected, show `n items`
		if (selected.length > 1) return { selectedLabel: `${selected.length} items` };
		// if there's one item selected, show its label
		if (selected.length === 1) return { selectedLabel: options[selected].label };
		// otherwise default to the default label
		return { selectedLabel: defaultLabel };
	})
);


export const MultiSelect = ({
	name,
	selected,
	selectedLabel,
	options,
	label,
	handleChange,
	multiple,
	menuExpanded,
	toggleMenuExpanded,
	icon,
	closeMenu,
	openMenu,
	segmentLabel
}) => (
	<div className={classnames('ss-multiselect', { unique: !multiple })}>
		{label && (
			<Text size="tiny" weight="standard">
				{label}
			</Text>
		)}
		<div
			className="control"
			onMouseEnter={() => openMenu()}
			onMouseLeave={() => closeMenu()}
		>
			<button
				className="top"
				type="button"
				onClick={() => toggleMenuExpanded()}
			>
				{icon && icon}
				<Text last>{selectedLabel}</Text>
				<Chevron direction={menuExpanded ? 'up' : 'down'} />
			</button>
			<div
				className={classnames('menu', { expanded: menuExpanded })}
			>
				<ul className="options-list">
					{Object.values(options).map((option) => (
						<li className="options-list-item" key={option.value}>
							<MultiSelectOption
								name={name}
								onChange={handleChange}
								segmentLabel={segmentLabel}
								multiple={multiple}
								selected={selected.includes(option.value)}
								value={option.value}
								label={option.label}
							/>
						</li>
					))}
				</ul>
			</div>
		</div>
	</div>
);

MultiSelect.defaultProps = {
	selected: [],
	multiple: true,
	icon: null
};

MultiSelect.propTypes = {
	icon: PropTypes.node,
	name: PropTypes.string.isRequired,
	selected: PropTypes.arrayOf(PropTypes.string),
	selectedLabel: PropTypes.string,
	options: PropTypes.objectOf(
		PropTypes.shape({
			value: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired
		})
	).isRequired,
	label: PropTypes.string,
	handleChange: PropTypes.func,
	toggleMenuExpanded: PropTypes.func,
	menuExpanded: PropTypes.bool,
	multiple: PropTypes.bool,
	closeMenu: PropTypes.func,
	openMenu: PropTypes.func,
	segmentLabel: PropTypes.string.isRequired
};

export default enhance(MultiSelect);
